import { useState } from "react";
import {
  useCBenef,
  useCstIcmsCombo,
  useCsosnCombo,
} from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { CBenefDto, SalvarCBenefRequest } from "@/features/fiscal/types";
import { ProdutoSearchCombobox } from "@/components/ProdutoSearchCombobox";
import { RegraFiscalSearchCombobox, RegraFiscalRotulo } from "@/components/RegraFiscalSearchCombobox";
import { ProdutoNome } from "@/components/ProdutoNome";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Gift, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

// ── Estados brasileiros ────────────────────────────────────────────────────
const UFS = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO",
  "MA","MG","MS","MT","PA","PB","PE","PI","PR",
  "RJ","RN","RO","RR","RS","SC","SE","SP","TO",
];

type Regime = "simples" | "normal";

interface FormState extends SalvarCBenefRequest {
  regime: Regime;
}

const EMPTY: FormState = {
  cbenef:        "",
  uf:            "",
  produtoId:     0,
  regraFiscalId: 0,
  cstId:         null,
  regime:        "normal",
};

export default function BeneficioFiscal() {
  const [busca, setBusca]           = useState("");
  const [ufFiltro, setUfFiltro]     = useState("");
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<FormState>(EMPTY);
  const [saving, setSaving]         = useState(false);

  const { query, obterPorId, criar, atualizar, excluir } = useCBenef({
    busca:  busca || undefined,
    uf:     ufFiltro || undefined,
    pagina,
    tamanhoPagina,
  });

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  // Dados para combos do dialog
  const { data: cstIcmsOptions = []  } = useCstIcmsCombo();
  const { data: csosnOptions    = []  } = useCsosnCombo();
  const itens = query.data?.itens ?? [];

  // Resolve IDs -> rótulos legíveis para a lista (regra: <RegraFiscalRotulo>, sem o teto de 200)
  const cstLabel = (id: number | null) => {
    if (id == null) return "—";
    const cst = cstIcmsOptions.find(c => c.id === id);
    if (cst) return `${cst.cst} · CST`;
    const cso = csosnOptions.find(c => c.id === id);
    if (cso) return `${cso.codigoCsosn} · CSOSN`;
    return "—";
  };

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm(f => ({ ...f, [field]: value }));

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    try {
      const dto: CBenefDto = await obterPorId(id);
      setEditId(id);
      setForm({
        cbenef:        dto.cbenef,
        uf:            dto.uf,
        produtoId:     dto.produtoId,
        regraFiscalId: dto.regraFiscalId,
        cstId:         dto.cstId,
        // Detecta regime pela presença do ID em cada lista
        regime: csosnOptions.some(c => c.id === dto.cstId) ? "simples" : "normal",
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar benefício fiscal.");
    }
  };

  const handleSave = async () => {
    if (!form.cbenef.trim()) { toast.error("Código CBenef é obrigatório."); return; }
    if (!form.uf)            { toast.error("UF é obrigatória."); return; }
    if (!form.produtoId)     { toast.error("Produto é obrigatório."); return; }
    if (!form.regraFiscalId) { toast.error("Regra Fiscal é obrigatória."); return; }

    const req: SalvarCBenefRequest = {
      cbenef:        form.cbenef,
      uf:            form.uf,
      produtoId:     form.produtoId,
      regraFiscalId: form.regraFiscalId,
      cstId:         form.cstId,
    };

    setSaving(true);
    try {
      if (editId) {
        await atualizar.mutateAsync({ id: editId, req });
        toast.success("Benefício fiscal atualizado.");
      } else {
        await criar.mutateAsync(req);
        toast.success("Benefício fiscal criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar benefício fiscal.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("Benefício fiscal excluído.");
    } catch {
      toast.error("Erro ao excluir benefício fiscal.");
    } finally {
      setDeleteId(null);
    }
  };

  // Limpa o cstId ao trocar de regime
  const handleRegimeChange = (v: Regime) =>
    setForm(f => ({ ...f, regime: v, cstId: null }));

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gift className="h-6 w-6" /> Código de Benefício Fiscal
          </h1>
          <p className="text-muted-foreground">CBenef — código de benefício fiscal por produto e UF</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" /> Novo CBenef
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por CBenef ou UF..."
            className="pl-9"
            value={busca}
            onChange={e => { setBusca(e.target.value); setPagina(1); }}
          />
        </div>
        <Select value={ufFiltro} onValueChange={v => { setUfFiltro(v === "_all" ? "" : v); setPagina(1); }}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Todas UF" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todas UF</SelectItem>
            {UFS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">CBenef</TableHead>
              <TableHead className="w-[60px]">UF</TableHead>
              <TableHead>Produto</TableHead>
              <TableHead>Regra Fiscal</TableHead>
              <TableHead className="w-[130px]">CST / CSOSN</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
              </TableRow>
            ) : itens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nenhum registro encontrado.</TableCell>
              </TableRow>
            ) : itens.map(item => (
              <TableRow key={item.id}>
                <TableCell className="font-mono font-semibold">{item.cbenef}</TableCell>
                <TableCell>
                  <span className="inline-block bg-muted rounded px-2 py-0.5 text-xs font-medium">{item.uf}</span>
                </TableCell>
                <TableCell className="text-sm">{item.produtoNome ?? (item.produtoId ? <ProdutoNome id={item.produtoId} /> : "—")}</TableCell>
                <TableCell className="text-sm text-muted-foreground"><RegraFiscalRotulo id={item.regraFiscalId} /></TableCell>
                <TableCell className="text-sm">{cstLabel(item.cstId)}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(item.id)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteId(item.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog Criar / Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Benefício Fiscal" : "Novo Benefício Fiscal"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Código CBenef + UF */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Código CBenef <span className="text-destructive">*</span></Label>
                <Input
                  value={form.cbenef}
                  onChange={e => set("cbenef", e.target.value)}
                  maxLength={10}
                  placeholder="Ex.: SC820380"
                />
              </div>
              <div className="space-y-1">
                <Label>UF <span className="text-destructive">*</span></Label>
                <Select value={form.uf} onValueChange={v => set("uf", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {UFS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Produto */}
            <div className="space-y-1">
              <Label>Produto <span className="text-destructive">*</span></Label>
              <ProdutoSearchCombobox
                value={form.produtoId || undefined}
                onChange={v => set("produtoId", v)}
              />
            </div>

            {/* Regra Fiscal */}
            <div className="space-y-1">
              <Label>Regra Fiscal <span className="text-destructive">*</span></Label>
              <RegraFiscalSearchCombobox
                value={form.regraFiscalId || null}
                onChange={id => set("regraFiscalId", id)}
              />
            </div>

            {/* Regime + CST/CSOSN */}
            <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
              <Label className="text-sm font-medium">Regime Tributário</Label>
              <RadioGroup
                value={form.regime}
                onValueChange={v => handleRegimeChange(v as Regime)}
                className="flex gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="normal" id="regime-normal" />
                  <Label htmlFor="regime-normal" className="cursor-pointer font-normal">
                    Regime Normal (CST ICMS)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="simples" id="regime-simples" />
                  <Label htmlFor="regime-simples" className="cursor-pointer font-normal">
                    Simples Nacional (CSOSN)
                  </Label>
                </div>
              </RadioGroup>

              {/* CST ICMS — Regime Normal */}
              {form.regime === "normal" && (
                <div className="space-y-1 pt-1">
                  <Label className="text-xs text-muted-foreground">CST ICMS</Label>
                  <Select
                    value={form.cstId ? String(form.cstId) : ""}
                    onValueChange={v => set("cstId", v ? Number(v) : null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o CST..." />
                    </SelectTrigger>
                    <SelectContent>
                      {cstIcmsOptions.map(c => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.cst} — {c.descricao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* CSOSN — Simples Nacional */}
              {form.regime === "simples" && (
                <div className="space-y-1 pt-1">
                  <Label className="text-xs text-muted-foreground">CSOSN</Label>
                  <Select
                    value={form.cstId ? String(form.cstId) : ""}
                    onValueChange={v => set("cstId", v ? Number(v) : null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o CSOSN..." />
                    </SelectTrigger>
                    <SelectContent>
                      {csosnOptions.map(c => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.codigoCsosn} — {c.descricao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir benefício fiscal?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
